import { Text } from '@mantine/core';
import { useDebouncedValue, useDidUpdate, useListState, useToggle } from '@mantine/hooks';
import { openConfirmModal } from '@mantine/modals';
import { useAtomValue } from 'jotai';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { mergedEventListingsUnfilteredRulesAtom } from '../../data/atoms';
import { SortItem } from './Inventory.SortItem';
import { onColumnMoved, onColumnSorted, useInventory } from './Inventory.hooks';
import yasml from '@thirtytech/yasml';
import { ColumnPinnedType } from 'ag-grid-community';
import { seasons } from '../../data/atoms.seasons';

type ColumnSettingItem = {
  sortIndex?: number;
  key: string;
  name: string;
  visible: boolean; // TODO: Rename this to hide to match the grid eventually.
  width: number | undefined;
  pinned: ColumnPinnedType;
};

const ALLOWED_SORTING_EVENT_COLUMNS: SortItem[] = [
  { key: 'event.localDateTime', name: 'Date', dir: 'asc' },
  { key: 'event.name', name: 'Name', dir: null },
  { key: 'event.venue.name', name: 'Venue Name', dir: null },
  { key: 'event.venue.city', name: 'City', dir: null },
  { key: 'event.openCost', name: 'Open Cost', dir: null },
  { key: 'event.openTickets', name: 'Open Tickets', dir: null },
  { key: 'event.openListings', name: 'Open Listings', dir: null },
];
ALLOWED_SORTING_EVENT_COLUMNS.sort((a, b) => a.name.localeCompare(b.name));
const InventoryHeaderState = () => {
  const { inventoryQuickFilter, setInventoryQuickFilter, toggleShowSort, gridRef, gridReady, clearInventoryGridState, inventoryGridState, defaultColumnDefs, filter, setFilter } =
    useInventory(
      'inventoryQuickFilter',
      'setInventoryQuickFilter',
      'toggleShowSort',
      'gridRef',
      'gridReady',
      'clearInventoryGridState',
      'inventoryGridState',
      'defaultColumnDefs',
      'filter',
      'setFilter',
    );

  const selectedSeason = useAtomValue(seasons.selectedSeasonAtom);
  const allListings = useAtomValue(mergedEventListingsUnfilteredRulesAtom);
  const [showSettings, setShowSettings] = useState(false);
  const [listingSort, listingSortHandlers] = useListState<SortItem>([]);
  const [columnSettings, columnSettingsHandlers] = useListState<ColumnSettingItem>([]);
  const [enableUpdates, toggleUpdates] = useToggle();
  const inventoryGridStateRef = useRef(inventoryGridState);
  const [columnSettingsDebounced] = useDebouncedValue(columnSettings, 250);
  const [listingSortDebounced] = useDebouncedValue(listingSort, 250);

  const allowedSortingColumns = useMemo(
    () =>
      structuredClone(
        defaultColumnDefs
          .filter((x) => x.sortIndex != null || x.allowSorting !== false)
          .map((x) => ({ key: x.field || x.colId!, name: x.headerName || x.field || x.colId!, dir: x.sort, sortIndex: x.sortIndex })),
      ).sort((a, b) => {
        const aIndex = typeof a.sortIndex === 'number' ? a.sortIndex : Infinity;
        const bIndex = typeof b.sortIndex === 'number' ? b.sortIndex : Infinity;
        return aIndex - bIndex || (a.dir || '').localeCompare(b.dir || '') || a.name.localeCompare(b.name);
      }),
    [defaultColumnDefs],
  );

  const defaultColumnSettings = useMemo(
    () =>
      defaultColumnDefs
        .filter((x) => !x.exclude)
        .map(
          (x) =>
            ({
              key: x.field || x.colId!,
              name: x.headerName!,
              visible: !x.hide,
              width: x.width ?? undefined,
              pinned: x.colId === 'action' ? 'right' : undefined,
            }) satisfies ColumnSettingItem,
        ),
    [defaultColumnDefs],
  );

  useDidUpdate(() => {
    inventoryGridStateRef.current = inventoryGridState;
  }, [inventoryGridState]);

  const resetListingDefaults = useCallback(() => {
    listingSortHandlers.setState(allowedSortingColumns);
  }, [allowedSortingColumns, listingSortHandlers]);

  const resetColumnDefaults = useCallback(() => {
    columnSettingsHandlers.setState(defaultColumnSettings);
  }, [defaultColumnSettings, columnSettingsHandlers]);

  const resetAllDefaults = useCallback(() => {
    openConfirmModal({
      title: 'Reset confirmation',
      children: <Text size="sm">Are you sure you want to reset all the grid settings to their defaults?</Text>,
      labels: { confirm: 'Reset', cancel: 'Cancel' },
      confirmProps: { className: 'confirmButton', variant: 'filled', color: 'gray', size: 'sm' },
      cancelProps: { className: 'cancelButton', variant: 'default', size: 'sm' },
      closeButtonProps: { size: 'md' },
      onConfirm: () => {
        clearInventoryGridState();
        resetColumnDefaults();
        resetListingDefaults();
      },
    });
  }, [clearInventoryGridState, resetColumnDefaults, resetListingDefaults]);

  useDidUpdate(() => {
    if (enableUpdates) {
      const updateOrder = listingSortDebounced.map((x, i) => ({ colId: x.key, sort: x.dir, sortIndex: i }));
      gridRef.current?.api.applyColumnState({
        state: updateOrder,
      });
    }
  }, [listingSortDebounced]);

  useDidUpdate(() => {
    if (enableUpdates) {
      gridRef.current?.api.applyColumnState({
        applyOrder: true,
        state: [{ colId: 'dragCell' }, ...columnSettingsDebounced.map((x) => ({ colId: x.key, hide: !x.visible, width: x.width, pinned: x.pinned }))],
      });
    }
  }, [columnSettingsDebounced]);

  const isMenuOpened = useRef(false);
  const onMenuVisibilityChange = useCallback(() => {
    isMenuOpened.current = !isMenuOpened.current;
  }, []);

  const processListingSort = useCallback(
    ({ initialRender }: { initialRender?: boolean } = { initialRender: false }) => {
      const columnState = gridRef.current!.api.getColumnState();
      const columnSort = columnState
        .sort((x) => x.sortIndex || Infinity)
        .map((x) => {
          const columnDef = gridRef.current?.api.getColumn(x.colId)?.getColDef();
          return {
            key: x.colId,
            name: columnDef?.headerName ?? columnDef?.field ?? '',
            dir: x.sort,
            sortIndex: x.sortIndex,
          } satisfies SortItem;
        });

      // Precompute the lowercased allowed keys once; previously this array was rebuilt + scanned per column (O(n*m)).
      const allowedKeysLower = new Set(allowedSortingColumns.map((y) => y.key.toLowerCase()));
      // .filter already returns a fresh array, so the previous [...] spread copy was redundant.
      let finalListingSort = columnSort.filter((x) => allowedKeysLower.has(x.key.toLowerCase()));
      if (initialRender) {
        // Index saved state by colId once; previously this was a linear find per row (O(n*m)).
        // colIds are unique in AG-Grid column state, so the first-match semantics of .find are preserved.
        const savedStateByColId = new Map((inventoryGridStateRef.current ?? []).map((y) => [y.colId, y]));
        finalListingSort = structuredClone(
          finalListingSort.map((x) => {
            const savedState = savedStateByColId.get(x.key) || {};
            const initialValues = allowedSortingColumns.find((y) => y.key === x.key) || {};
            return { ...x, ...initialValues, ...savedState };
          }),
        );
      }
      finalListingSort.sort((a, b) => {
        const aIndex = typeof a.sortIndex === 'number' ? a.sortIndex : Infinity;
        const bIndex = typeof b.sortIndex === 'number' ? b.sortIndex : Infinity;
        return aIndex - bIndex || (a.dir || '').localeCompare(b.dir || '') || a.name.localeCompare(b.name);
      });
      listingSortHandlers.setState(finalListingSort);
    },
    [allowedSortingColumns, gridRef, listingSortHandlers],
  );

  const processColumnSettings = useCallback(() => {
    // Set lookup keeps the filter O(1) per column instead of scanning the ignored list each time.
    const ignoredColIds = new Set(defaultColumnDefs.filter((x) => x.exclude).map((x) => x.field || x.colId!));
    const columns = gridRef.current!.api.getColumns();
    const columnState = gridRef.current!.api.getColumnState()!;
    // Index columns by colId once so the per-column lookup below is O(1) instead of a linear find.
    const columnsByColId = new Map(columns?.map((x) => [x.getColId(), x]));
    const _columnSettingItems = columnState
      .filter((col) => !ignoredColIds.has(col.colId))
      .map((col) => {
        const columnDef = columnsByColId.get(col.colId)?.getColDef();
        return {
          key: col.colId,
          name: columnDef?.headerName ?? columnDef?.field ?? '',
          visible: !col.hide,
          width: col.width ?? undefined,
          pinned: col.pinned,
          sortIndex: col.sortIndex || 0,
        };
      });

    columnSettingsHandlers.setState(_columnSettingItems);
  }, [columnSettingsHandlers, defaultColumnDefs, gridRef]);

  const onColumnMovedEvt = useCallback(() => {
    if (!isMenuOpened.current) {
      processColumnSettings();
    }
  }, [processColumnSettings]);

  const onColumnSortedEvt = useCallback(() => {
    if (!isMenuOpened.current) {
      processListingSort();
    }
  }, [processListingSort]);

  useEffect(() => {
    if (enableUpdates) {
      document.addEventListener(onColumnMoved.type, onColumnMovedEvt);
      document.addEventListener(onColumnSorted.type, onColumnSortedEvt);
    }
    return () => {
      document.removeEventListener(onColumnMoved.type, onColumnMovedEvt);
      document.removeEventListener(onColumnSorted.type, onColumnSortedEvt);
    };
  }, [enableUpdates, onColumnMovedEvt, onColumnSortedEvt]);

  useEffect(() => {
    if (gridReady) {
      processListingSort({ initialRender: true });
      processColumnSettings();

      setTimeout(() => toggleUpdates(true), 0);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [gridReady]);

  const toggleSettings = useCallback(() => {
    setShowSettings(!showSettings);
    toggleShowSort();
  }, [showSettings, toggleShowSort]);

  return {
    selectedSeason,
    columnSettings,
    columnSettingsHandlers,
    filter,
    setFilter,
    setInventoryQuickFilter,
    inventoryQuickFilter,
    showSettings,
    listingSort,
    listingSortHandlers,
    resetAllDefaults,
    resetColumnDefaults,
    resetListingDefaults,
    toggleSettings,
    onMenuVisibilityChange,
    allListings,
  };
};

export const { Provider: InventoryHeaderStateProvider, useSelector: useInventoryHeader } = yasml(InventoryHeaderState);
